/**
 * Secure Cookie Service for managing authentication tokens and user data
 * Uses HttpOnly cookies for enhanced security
 */

export interface CookieOptions {
  expires?: Date;
  maxAge?: number;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  httpOnly?: boolean;
}

export class CookieService {
  private static readonly DEFAULT_OPTIONS: CookieOptions = {
    path: '/',
    secure: process.env.NODE_ENV === 'production',
    sameSite: 'strict',
    httpOnly: false, // Set to false for client-side access, backend should set httpOnly
  };

  /**
   * Set a cookie with secure defaults
   */
  static setCookie(name: string, value: string, options: CookieOptions = {}): void {
    const cookieOptions = { ...this.DEFAULT_OPTIONS, ...options };
    
    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
    
    if (cookieOptions.expires) {
      cookieString += `; expires=${cookieOptions.expires.toUTCString()}`;
    }
    
    if (cookieOptions.maxAge) {
      cookieString += `; max-age=${cookieOptions.maxAge}`;
    }
    
    if (cookieOptions.path) {
      cookieString += `; path=${cookieOptions.path}`;
    }
    
    if (cookieOptions.domain) {
      cookieString += `; domain=${cookieOptions.domain}`;
    }
    
    if (cookieOptions.secure) {
      cookieString += `; secure`;
    }
    
    if (cookieOptions.sameSite) {
      cookieString += `; samesite=${cookieOptions.sameSite}`;
    }
    
    if (cookieOptions.httpOnly) {
      cookieString += `; httponly`;
    }
    
    document.cookie = cookieString;
  }

  /**
   * Get a cookie value by name
   */
  static getCookie(name: string): string | null {
    const nameEQ = encodeURIComponent(name) + '=';
    const cookies = document.cookie.split(';');
    
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length));
      }
    }
    
    return null;
  }

  /**
   * Remove a cookie by setting it to expire in the past
   */
  static removeCookie(name: string, options: CookieOptions = {}): void {
    const cookieOptions = { ...this.DEFAULT_OPTIONS, ...options };
    cookieOptions.expires = new Date(0);
    this.setCookie(name, '', cookieOptions);
  }

  /**
   * Check if a cookie exists
   */
  static hasCookie(name: string): boolean {
    return this.getCookie(name) !== null;
  }

  /**
   * Set authentication token with secure defaults
   */
  static setAuthToken(token: string, maxAge: number = 24 * 60 * 60): void {
    this.setCookie('auth_token', token, {
      maxAge,
      secure: true,
      sameSite: 'strict',
      httpOnly: false, // Client needs access for API calls
    });
  }

  /**
   * Get authentication token
   */
  static getAuthToken(): string | null {
    return this.getCookie('auth_token');
  }

  /**
   * Remove authentication token
   */
  static removeAuthToken(): void {
    this.removeCookie('auth_token', {
      secure: true,
      sameSite: 'strict',
    });
  }

  /**
   * Set user data in cookie
   */
  static setUserData(userData: any, maxAge: number = 24 * 60 * 60): void {
    const userDataString = JSON.stringify(userData);
    this.setCookie('user_data', userDataString, {
      maxAge,
      secure: true,
      sameSite: 'strict',
      httpOnly: false,
    });
  }

  /**
   * Get user data from cookie
   */
  static getUserData(): any | null {
    const userDataString = this.getCookie('user_data');
    if (!userDataString) return null;
    
    try {
      return JSON.parse(userDataString);
    } catch (error) {
      console.error('Error parsing user data from cookie:', error);
      return null;
    }
  }

  /**
   * Remove user data cookie
   */
  static removeUserData(): void {
    this.removeCookie('user_data', {
      secure: true,
      sameSite: 'strict',
    });
  }

  /**
   * Set impersonation status
   */
  static setImpersonating(isImpersonating: boolean, maxAge: number = 24 * 60 * 60): void {
    this.setCookie('b_impersonating', JSON.stringify(isImpersonating), {
      maxAge,
      secure: true,
      sameSite: 'strict',
      httpOnly: false,
    });
  }

  /**
   * Get impersonation status
   */
  static getImpersonating(): boolean {
    const value = this.getCookie('b_impersonating');
    if (!value) return false;
    
    try {
      return JSON.parse(value);
    } catch (error) {
      console.error('Error parsing impersonation status from cookie:', error);
      return false;
    }
  }

  /**
   * Remove impersonation status cookie
   */
  static removeImpersonating(): void {
    this.removeCookie('b_impersonating', {
      secure: true,
      sameSite: 'strict',
    });
  }

  /**
   * Clear all authentication-related cookies
   */
  static clearAuthCookies(): void {
    this.removeAuthToken();
    this.removeUserData();
    this.removeImpersonating();
  }
}
